import React, { Component } from "react";
import { C, PillToggle } from "../Console/ConsoleUI";
import {
  PERIOD_MODES,
  MONTH_NAMES,
  periodBounds,
  periodError,
  lastDays,
  thisMonth,
  thisYear,
  today,
  hotelNow,
} from "../../utils/period";

export * from "../../utils/period";

/**
 * The period picker every analytics, report and dated-list screen shares:
 * All time · Month (month + year) · Year · Custom range (start → end).
 *
 *   <PeriodFilter value={this.state.period} onChange={(period) => ...} />
 *
 * `value` is a period object (see utils/period.js); pass it through
 * periodToParams() when calling the API. onChange only ever fires with a
 * complete, valid period — a half-typed range stays a local draft with an
 * inline error — so a page can refetch on every change without guarding.
 *
 * `withToday` adds a leading "Today" pill (a one-day range, see today()) for
 * the live dashboard; every other screen offers just the four choices.
 *
 * Plain inputs styled from the console tokens rather than reactstrap, so it
 * reads the same on console screens and on the older Bootstrap ones.
 */

const TODAY_MODE = { value: "today", label: "Today" };

const control = {
  height: 42, padding: "0 12px", borderRadius: 12, background: "#fff",
  border: `1px solid ${C.border}`, fontSize: 14, color: C.ink,
  fontFamily: "inherit", outline: "none",
};

class PeriodFilter extends Component {
  static defaultProps = { minYear: 2020 };

  constructor(props) {
    super(props);
    this.state = this.draftFrom(props.value);
  }

  componentDidUpdate(prevProps) {
    const a = prevProps.value || {};
    const b = this.props.value || {};
    if (a.mode !== b.mode || a.startDate !== b.startDate || a.endDate !== b.endDate) {
      this.setState(this.draftFrom(b));
    }
  }

  draftFrom = (value) => ({
    draftStart: (value && value.startDate) || "",
    draftEnd: (value && value.endDate) || "",
  });

  emit = (next) => {
    if (!periodError(next) && this.props.onChange) this.props.onChange(next);
  };

  /** Switching mode keeps as much of the current window as the new mode can hold. */
  handleMode = (mode) => {
    const current = this.props.value || {};
    if (mode === (current.preset || current.mode)) return;
    const bounds = periodBounds(current);
    const now = hotelNow();
    if (mode === "all") return this.emit({ mode: "all" });
    if (mode === "today") return this.emit(today(now));
    if (mode === "year") return this.emit({ mode: "year", year: current.year || thisYear(now).year });
    if (mode === "month") {
      const fallback = thisMonth(now);
      // Moving from a year keeps that year; its month is this month if that's the current year.
      const year = current.year || fallback.year;
      const month = current.month || (year === fallback.year ? fallback.month : 1);
      return this.emit({ mode: "month", month, year });
    }
    this.emit(bounds ? { mode: "range", ...bounds } : lastDays(30, now));
  };

  handleMonth = (e) => {
    const { value } = this.props;
    this.emit({ mode: "month", month: Number(e.target.value), year: value.year });
  };

  handleYear = (e) => {
    const { value } = this.props;
    const year = Number(e.target.value);
    if (value.mode === "year") return this.emit({ mode: "year", year });
    // A future month in the newly picked year would only ever show zeros.
    const now = hotelNow();
    const month = year === now.getFullYear() ? Math.min(value.month, now.getMonth() + 1) : value.month;
    this.emit({ mode: "month", month, year });
  };

  handleDate = (key) => (e) => {
    this.setState({ [key]: e.target.value }, () => {
      this.emit({ mode: "range", startDate: this.state.draftStart, endDate: this.state.draftEnd });
    });
  };

  years() {
    const { minYear, value } = this.props;
    const max = hotelNow().getFullYear();
    const min = Math.min(minYear, (value && value.year) || max);
    const out = [];
    for (let y = max; y >= min; y -= 1) out.push(y);
    return out;
  }

  renderControls() {
    const { value, disabled } = this.props;
    const now = hotelNow();

    if (value.mode === "month" || value.mode === "year") {
      const futureMonth = (m) => value.year === now.getFullYear() && m > now.getMonth() + 1;
      return (
        <React.Fragment>
          {value.mode === "month" && (
            <select aria-label="Month" value={value.month} onChange={this.handleMonth}
              disabled={disabled} style={control}>
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i + 1} disabled={futureMonth(i + 1)}>{name}</option>
              ))}
            </select>
          )}
          <select aria-label="Year" value={value.year} onChange={this.handleYear}
            disabled={disabled} style={control}>
            {this.years().map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </React.Fragment>
      );
    }

    if (value.mode === "range" && !value.preset) {
      const { draftStart, draftEnd } = this.state;
      return (
        <React.Fragment>
          <input type="date" aria-label="Start date" value={draftStart} max={draftEnd || undefined}
            onChange={this.handleDate("draftStart")} disabled={disabled} style={control} />
          <span style={{ fontSize: 14, color: C.taupe }}>to</span>
          <input type="date" aria-label="End date" value={draftEnd} min={draftStart || undefined}
            onChange={this.handleDate("draftEnd")} disabled={disabled} style={control} />
        </React.Fragment>
      );
    }

    return null;
  }

  render() {
    const { value, style, withToday } = this.props;
    const modes = withToday ? [TODAY_MODE, ...PERIOD_MODES] : PERIOD_MODES;
    const draftError = value.mode === "range"
      ? periodError({ mode: "range", startDate: this.state.draftStart, endDate: this.state.draftEnd })
      : null;

    return (
      <div style={style}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <PillToggle options={modes} value={value.preset || value.mode} onChange={this.handleMode} />
          {this.renderControls()}
        </div>
        {draftError && <div style={{ marginTop: 6, fontSize: 12, color: C.red }}>{draftError}</div>}
      </div>
    );
  }
}

export default PeriodFilter;
